import React from 'react';
import { canUserEnterComp, compMembershipRestrictionText, membershipTypeAllowedForComp } from '../utils/membership';
import { NON_MEMBER_GUEST_MEMBERSHIP_TYPE } from '../constants/membership';
import { compTakesTeamEntries, compBuildsTeamsFromDraw } from '../utils/competition';
import MyClubTeamPicker, { clubTeamEntry, useMyClubTeams } from './MyClubTeamPicker';

/* ====================================================================
   PLAYER REGISTRATION BUTTON — for competition home page
   ==================================================================== */
function CompRegistrationButton({ API, compId, currentUser, comp, onRegistered }) {
  const [myReg, setMyReg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [teamName, setTeamName] = React.useState('');
  const [teamMembers, setTeamMembers] = React.useState([{ id: '', name: '', email: '' }]);
  const [clubPlayers, setClubPlayers] = React.useState([]);
  const [playerSearch, setPlayerSearch] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  // The club team being entered: an id, 'new' for a team typed in by hand,
  // or null while the player has yet to choose.
  const [pickedTeam, setPickedTeam] = React.useState(null);
  const myClubTeams = useMyClubTeams(API, comp, currentUser.id);

  const pickClubTeam = (ct) => {
    const entry = clubTeamEntry(ct, comp, currentUser.id, clubPlayers);
    setPickedTeam(ct.id);
    setError('');
    setTeamName(entry.teamName);
    setTeamMembers(entry.teamMembers);
  };

  const enterNewTeam = () => {
    setPickedTeam('new');
    setError('');
    setTeamName('');
    setTeamMembers([{ id: '', name: '', email: '' }]);
  };

  React.useEffect(() => {
    fetch(`${API}/api/registrations/${compId}/my`).then(r => r.json()).then(setMyReg).catch(() => {}).finally(() => setLoading(false));
    fetch(`${API}/api/users`).then(r => r.json()).then(setClubPlayers).catch(() => {});
  }, [API, compId]);

  const selectPlayer = (i, userId) => {
    if (!userId) {
      setTeamMembers(p => p.map((x, j) => j === i ? { id: '', name: '', email: '' } : x));
      return;
    }
    const pl = clubPlayers.find(u => u.id === parseInt(userId));
    if (pl) setTeamMembers(p => p.map((x, j) => j === i ? { id: pl.id, name: pl.name, email: pl.email || '' } : x));
  };

  const handleRegister = async () => {
    if (compTakesTeamEntries(comp)) {
      const requiredExtraPlayers = Math.max(0, (comp.playersPerTeam || 1) - 1);
      const providedExtraPlayers = teamMembers.filter(m => String(m.name || '').trim()).length;
      if (providedExtraPlayers < requiredExtraPlayers) {
        setError(`Add ${requiredExtraPlayers} team member${requiredExtraPlayers === 1 ? '' : 's'} before submitting registration.`);
        return;
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const body = {};
      if (compTakesTeamEntries(comp) && teamName.trim()) {
        body.teamName = teamName.trim();
        if (pickedTeam && pickedTeam !== 'new') body.clubTeamId = pickedTeam;
        body.teamMembers = teamMembers.filter(m => m.name.trim()).map(m => ({ id: m.id || undefined, name: m.name, email: m.email }));
      }
      const r = await fetch(`${API}/api/registrations/${compId}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) { setMyReg(data); if (onRegistered) onRegistered(); }
      else { setError(data.error || 'Registration failed'); }
    } catch (err) { setError('Network error — please try again'); }
    setSubmitting(false);
  };

  // For singles: register immediately on button click (no form needed)
  const handleSinglesRegister = async () => {
    setSubmitting(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/registrations/${compId}/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await r.json();
      if (r.ok) { setMyReg(data); if (onRegistered) onRegistered(); }
      else { setError(data.error || 'Registration failed'); }
    } catch (err) { setError('Network error — please try again'); }
    setSubmitting(false);
  };

  const handleCancel = async () => {
    setCancelling(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/registrations/${compId}/${myReg.id}`, { method: 'DELETE' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Cancellation failed');
      setMyReg({ ...myReg, status: 'withdrawn' });
      /* Withdrawing puts it back under "Open for entry" on the home page,
         which is where it belongs again. */
      if (onRegistered) onRegistered();
      setConfirmCancel(false);
    } catch (err) {
      setError(err.message || 'Cancellation failed');
    }
    setCancelling(false);
  };

  if (loading) return null;

  const canRetry = myReg && ['rejected', 'withdrawn'].includes(myReg.status);

  if (myReg && !canRetry) {
    return (
      <div className={`w3-panel w3-round w3-padding ${myReg.status === 'approved' ? 'w3-pale-green' : myReg.status === 'pending' ? 'w3-pale-yellow' : 'w3-pale-red'}`} style={{ marginBottom: '16px' }}>
        <i className={`fa ${myReg.status === 'approved' ? 'fa-check-circle w3-text-green' : 'fa-clock-o w3-text-orange'} w3-margin-right`}></i>
        {myReg.status === 'approved' && <strong>You are registered for this competition</strong>}
        {myReg.status === 'pending' && <strong>Your registration is pending admin approval</strong>}
        {myReg.status === 'rejected' && <strong>Your registration was declined</strong>}
        {myReg.status === 'approved' && !confirmCancel && (
          <button className="w3-button w3-small w3-round w3-white w3-border w3-border-red w3-text-red w3-margin-left"
            onClick={() => setConfirmCancel(true)}>
            <i className="fa fa-times w3-margin-right"></i>Cancel Registration
          </button>
        )}
        {myReg.status === 'approved' && confirmCancel && (
          <span className="w3-margin-left">
            <span className="w3-small w3-text-red w3-margin-right">Remove you from this competition?</span>
            <button className="w3-button w3-small w3-round w3-red w3-margin-right" onClick={handleCancel} disabled={cancelling}>
              {cancelling ? <i className="fa fa-spinner fa-spin"></i> : 'Yes, cancel'}
            </button>
            <button className="w3-button w3-small w3-round w3-white w3-border" onClick={() => setConfirmCancel(false)} disabled={cancelling}>Keep</button>
          </span>
        )}
        {error && <div className="w3-text-red w3-small" style={{ width: '100%', marginTop: '6px' }}>{error}</div>}
      </div>
    );
  }

  // A comp limited to certain membership types is not offered to anyone else
  // — the server refuses the registration either way.
  if (!canUserEnterComp(comp, currentUser)) {
    return (
      <div className="w3-panel w3-round w3-pale-yellow w3-padding" style={{ marginBottom: '16px' }}>
        <i className="fa fa-id-card w3-margin-right"></i>
        <strong>Only open to: {compMembershipRestrictionText(comp)}</strong>
        <div className="w3-small w3-text-grey" style={{ marginTop: '4px' }}>
          You are registered as {String(currentUser.membershipType || '').trim() || 'no membership type'}.
          Update your membership with the club to enter this competition.
        </div>
      </div>
    );
  }

  const isTeamFormat = compTakesTeamEntries(comp);
  const teamsDrawn = compBuildsTeamsFromDraw(comp);
  // Team-mates have to clear the same rule, so only offer players who do.
  const availablePlayers = clubPlayers.filter(u => u.id !== currentUser.id && canUserEnterComp(comp, u));
  const guestsAllowed = membershipTypeAllowedForComp(comp, NON_MEMBER_GUEST_MEMBERSHIP_TYPE);
  const normalizedPlayerSearch = String(playerSearch || '').trim().toLowerCase();
  const requiredExtraPlayers = isTeamFormat ? Math.max(0, (comp.playersPerTeam || 1) - 1) : 0;
  const providedExtraPlayers = teamMembers.filter(m => String(m.name || '').trim()).length;
  const hasEnoughTeamMembers = !isTeamFormat || providedExtraPlayers >= requiredExtraPlayers;
  const choosingTeam = myClubTeams.length > 0 && pickedTeam === null;
  const pickedClubTeam = myClubTeams.find(ct => ct.id === pickedTeam);

  return (
    <div className="w3-panel w3-round w3-theme-l5 w3-padding" style={{ marginBottom: '16px' }}>
      {error && <div className="w3-panel w3-red w3-round w3-padding-small" style={{ marginBottom: '8px' }}><small>{error}</small></div>}
      {!isTeamFormat ? (
        /* One click, whether it is singles or a team competition whose teams
           the club draws: either way the player enters on their own. */
        <>
          <button className="w3-button w3-theme w3-round" onClick={handleSinglesRegister} disabled={submitting}>
            {submitting ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-user-plus w3-margin-right"></i>Register for this Competition</>}
          </button>
          {teamsDrawn && (
            <div className="w3-small w3-text-grey" style={{ marginTop: '6px' }}>
              Enter on your own — the draw puts you in a team of {comp.playersPerTeam}.
            </div>
          )}
        </>
      ) : !showForm ? (
        <button className="w3-button w3-theme w3-round" onClick={() => setShowForm(true)}>
          <i className="fa fa-user-plus w3-margin-right"></i>Register for this Competition
        </button>
      ) : choosingTeam ? (
        <div>
          <p className="w3-text-grey">Registering as <strong>{currentUser.name}</strong></p>
          <MyClubTeamPicker teams={myClubTeams} onPick={pickClubTeam} onNew={enterNewTeam} />
          <button className="w3-button w3-grey w3-round" onClick={() => setShowForm(false)}>Cancel</button>
        </div>
      ) : (
        <div>
          <p className="w3-text-grey">Registering as <strong>{currentUser.name}</strong></p>
          {isTeamFormat && (
            <>
              {myClubTeams.length > 0 && (
                <button className="w3-button w3-small w3-round w3-theme-l4 w3-margin-bottom" onClick={() => setPickedTeam(null)}>
                  <i className="fa fa-arrow-left w3-margin-right"></i>Choose a different team
                </button>
              )}
              <label className="w3-small w3-text-grey" style={{ display: 'block' }}>Team Name</label>
              {/* A club team keeps its own name; renaming it is done on the team, not on an entry. */}
              <input className="w3-input w3-border w3-round w3-margin-bottom" placeholder="e.g. The Kiwis"
                value={teamName} readOnly={!!pickedClubTeam}
                style={pickedClubTeam ? { background: 'var(--gs-bg)' } : undefined}
                onChange={e => setTeamName(e.target.value)} />
              <label className="w3-small w3-text-grey">Team Members</label>
              <input
                className="w3-input w3-border w3-round w3-margin-top w3-margin-bottom"
                placeholder="Search by member name or member ID"
                value={playerSearch}
                onChange={e => setPlayerSearch(e.target.value)}
              />
              {teamMembers.map((m, i) => (
                <div key={i} style={{ marginBottom: '6px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select className="w3-select w3-border w3-round" style={{ flex: 2 }}
                      value={m.id || ''}
                      onChange={e => {
                        if (e.target.value === '__new__') { selectPlayer(i, ''); }
                        else { selectPlayer(i, e.target.value); }
                      }}>
                      <option value="">-- Select player or type below --</option>
                      {/* The chosen player is always listed, so a team-mate filled in from a
                          club team still shows when the search or the club list leaves them out. */}
                      {m.id && <option value={m.id}>{m.name}</option>}
                      {availablePlayers
                        .filter(p => p.id !== m.id)
                        .filter(p => !teamMembers.some((tm, ti) => ti !== i && tm.id && tm.id === p.id))
                        .filter((p) => {
                          if (!normalizedPlayerSearch) return true;
                          const nameText = String(p.name || '').toLowerCase();
                          const memberIdText = String(p.membershipNumber || '').toLowerCase();
                          return nameText.includes(normalizedPlayerSearch) || memberIdText.includes(normalizedPlayerSearch);
                        })
                        .map(p => <option key={p.id} value={p.id}>{p.name} (ID: {p.membershipNumber || 'Not assigned'})</option>)}
                      {guestsAllowed && <option value="__new__">+ Add new player</option>}
                    </select>
                    {teamMembers.length > 1 && (
                      <button className="w3-button w3-round w3-tiny w3-red"
                        onClick={() => setTeamMembers(p => p.filter((_, j) => j !== i))}>
                        <i className="fa fa-times"></i>
                      </button>
                    )}
                  </div>
                  {!m.id && guestsAllowed && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <input className="w3-input w3-border w3-round" style={{ flex: 1 }} placeholder="Player name"
                        value={m.name} onChange={e => { const v = e.target.value; setTeamMembers(p => p.map((x, j) => j === i ? { ...x, name: v } : x)); }} />
                      <input className="w3-input w3-border w3-round" style={{ flex: 1 }} placeholder="Email (optional)"
                        value={m.email} onChange={e => { const v = e.target.value; setTeamMembers(p => p.map((x, j) => j === i ? { ...x, email: v } : x)); }} />
                    </div>
                  )}
                </div>
              ))}
              <button className="w3-button w3-tiny w3-theme-l4 w3-round w3-margin-top"
                onClick={() => setTeamMembers(p => [...p, { id: '', name: '', email: '' }])}>
                <i className="fa fa-plus w3-margin-right"></i>Add
              </button>
            </>
          )}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button className="w3-button w3-theme w3-round" onClick={handleRegister} disabled={submitting || (isTeamFormat && !hasEnoughTeamMembers)}>
              {submitting ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-check w3-margin-right"></i>Submit Registration</>}
            </button>
            {isTeamFormat && !hasEnoughTeamMembers && (
              <span className="w3-small w3-text-red" style={{ alignSelf: 'center' }}>
                Add {requiredExtraPlayers} team member{requiredExtraPlayers === 1 ? '' : 's'} (currently {providedExtraPlayers}) before submitting.
              </span>
            )}
            <button className="w3-button w3-grey w3-round" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          {isTeamFormat && (
            <div className="w3-small w3-text-grey" style={{ marginTop: '6px' }}>
              A confirmation email goes to every player on the team who has an email address.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CompRegistrationButton;
